import {
  Building2, GraduationCap, BookOpen, Landmark, BedDouble, Sprout,
  HeartPulse, Eye, Car, ShoppingBag, HardHat, Gem, Plus,
} from "lucide-react";
import { COMMERCIAL_PROJECTS, RESIDENTIAL_PROJECTS } from "@/data/content";

const ICONS = {
  Building2, GraduationCap, BookOpen, Landmark, BedDouble, Sprout,
  HeartPulse, Eye, Car, ShoppingBag, HardHat, Gem,
};

const Row = ({ items, reverse }) => (
  <div className="marquee-paused overflow-hidden">
    <div className={`marquee gap-4 py-2 ${reverse ? "marquee-reverse marquee-fast" : ""}`}>
      {[...items, ...items].map((p, i) => {
        const Icon = ICONS[p.icon] || Building2;
        return (
          <span
            key={`${p.name}-${i}`}
            className="group flex shrink-0 items-center gap-3.5 rounded-xl border border-slate-700/80 bg-slate-900/70 px-5 py-3.5 transition-all sm:px-6 sm:py-4 duration-300 hover:-translate-y-0.5 hover:border-brand/60 hover:bg-slate-900"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/5 text-slate-400 transition-colors duration-300 group-hover:bg-brand/15 group-hover:text-brand">
              <Icon size={18} />
            </span>
            <span className="text-sm font-semibold tracking-wide text-slate-200">{p.name}</span>
          </span>
        );
      })}
    </div>
  </div>
);

export default function ProjectsTicker({ divisionKey }) {
  const half = Math.ceil(COMMERCIAL_PROJECTS.length / 2);
  const toContact = () => {
    if (window.__lenis) window.__lenis.scrollTo("#contact", { offset: -72 });
    else document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="projects" className="scroll-mt-24 py-16 sm:py-20 lg:py-24" data-testid="projects-section">
      <div className="mx-auto mb-10 max-w-7xl px-4 sm:mb-12 sm:px-6 lg:px-8" data-reveal>
        <p className="kicker mb-4">Prestigious Projects</p>
        {divisionKey === "commercial" ? (
          <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Trusted by institutions, hotels & enterprises
          </h2>
        ) : (
          <>
            <h2 className="max-w-2xl font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Hundreds of homes executed
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Flawless electronic infrastructure for Flats, Villas, Bungalows, and Penthouses.
            </p>
          </>
        )}
      </div>

      {divisionKey === "commercial" ? (
        <div className="space-y-4" data-testid="projects-ticker" data-reveal>
          <Row items={COMMERCIAL_PROJECTS.slice(0, half)} />
          <Row items={COMMERCIAL_PROJECTS.slice(half)} reverse />
        </div>
      ) : (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-testid="projects-residential-showcase">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {RESIDENTIAL_PROJECTS.map((p, i) => (
              <figure
                key={p.title}
                data-reveal
                data-delay={i * 0.08}
                className={`group relative flex min-h-[20rem] flex-col justify-end overflow-hidden rounded-2xl border border-slate-700/70 transition-colors duration-500 hover:border-brand/60 ${
                  i === 0 ? "sm:col-span-2" : ""
                }`}
              >
                <img
                  src={p.img}
                  alt={`${p.title} — Bigboss Digitech residential project`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/25 to-transparent" />
                <figcaption className="relative p-5 pt-16">
                  <span className="font-tech text-[10px] uppercase tracking-[0.3em] text-brand">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-1 font-heading text-xl font-bold text-white">{p.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-300">{p.desc}</p>
                </figcaption>
              </figure>
            ))}
            <button
              data-testid="projects-much-more-card"
              data-reveal
              data-delay="0.32"
              onClick={toContact}
              className="group flex min-h-[20rem] flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-slate-600 bg-slate-900/40 p-6 text-center transition-all duration-500 hover:border-brand hover:bg-brand/5"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-slate-600 text-slate-300 transition-all duration-500 group-hover:rotate-90 group-hover:border-brand group-hover:bg-brand group-hover:text-white">
                <Plus size={22} />
              </span>
              <span className="font-heading text-xl font-bold text-white">And Much More</span>
              <span className="max-w-[220px] text-xs leading-relaxed text-slate-400">
                Flats, penthouses, farmhouses & every space in between.
              </span>
              <span className="font-tech text-[10px] uppercase tracking-[0.3em] text-brand">
                Tell us about your home
              </span>
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
